import { useEffect, useMemo, useRef, useState } from 'react';
import type { ContentRegistry } from '@/core/content/registry';
import type { ItemDef, WallFloorDef } from '@/core/content/schemas/items';
import { catalogNewIds } from '@/core/domain/economy';
import { unlockStatus } from '@/core/domain/unlocks';
import type { RotationKey } from '@/game/assets/manifest';
import type { MissingReason } from '@/core/domain/conditions';
import { swatchFor } from '@/ui/components/itemSwatch';
import { CoinIcon } from '@/ui/components/Icons';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useSaveDerived, useServices } from '@/ui/services/ServicesContext';
import { newTxId } from '@/ui/util/ids';
import { fold } from '@/ui/util/search';
import { ItemThumb } from '@/ui/art/ItemThumb';
import styles from './CatalogSheet.module.css';
import { useBack } from '@/ui/back/backStack';
import { Icon } from '@/ui/components/Icon';
import { SpotArt } from '@/ui/art/SpotArt';
import { storyStage } from '@/core/domain/neighborhood';
import { currentRoom } from '@/ui/util/room';

type Tab = 'furniture' | 'decor' | 'stock' | 'plants' | 'wallfloor' | 'themes';
const TABS: Tab[] = ['furniture', 'decor', 'stock', 'plants', 'wallfloor', 'themes'];

type Entry = { kind: 'item'; def: ItemDef } | { kind: 'wallfloor'; def: WallFloorDef };
type Sort = 'default' | 'priceUp' | 'priceDown' | 'new';
type Own = 'notOwned' | 'owned' | 'affordable';
const SORTS: Sort[] = ['default', 'priceUp', 'priceDown', 'new'];
const OWN_FILTERS: Own[] = ['notOwned', 'owned', 'affordable'];

/** What a tab lists (ux-screens §3.6 table). */
function entriesFor(content: ContentRegistry, tab: Tab): Entry[] {
  const items = [...content.items.values()].filter((d) => d.sources.some((s) => s.kind === 'catalog'));
  switch (tab) {
    case 'furniture':
      return items.filter((d) => d.kind === 'furniture').map((def) => ({ kind: 'item', def }));
    case 'themes':
      return items.filter((d) => d.setId).map((def) => ({ kind: 'item', def }));
    case 'decor':
      return items.filter((d) => d.kind === 'decor').map((def) => ({ kind: 'item', def }));
    case 'stock':
      return items
        .filter((d) => d.kind === 'product' || d.kind === 'fixture')
        .map((def) => ({ kind: 'item', def }));
    case 'plants':
      return items.filter((d) => d.themeTags.includes('garden')).map((def) => ({ kind: 'item', def }));
    case 'wallfloor':
      return [...content.wallFloors.values()].map((def) => ({ kind: 'wallfloor', def }));
  }
}

interface Props {
  onClose: () => void;
  onPlaceNow: () => void;
  initialTab?: Tab;
  /** Remembered for "nhớ tab cuối" and quick resume (ux-screens §6, §7.1). */
  onTabChange?: (tab: Tab) => void;
}

/**
 * S06 / S06a / S06b — shop catalog, item detail and purchase confirmation (ux-screens §3.6). Search, sort and
 * "Chưa có / Đã có / Mua được" filters; entries opened by a new unlock carry "Mới" until the player has seen
 * that tab (or the item) once.
 */
export function CatalogSheet({
  onClose: close,
  onPlaceNow: placeNow,
  initialTab = 'decor',
  onTabChange,
}: Props) {
  const { t } = useI18n();
  const { content, dispatch, store } = useServices();
  const coins = useSave((s) => s.wallet.coins);
  const instances = useSave((s) => s.inventory.instances);
  const stacks = useSave((s) => s.inventory.productStacks);
  const owned = useSave((s) => s.ownedWallFloors);
  const unlockedKey = useSave((s) => s.unlockedIds.join(','));
  const [tab, setTab] = useState<Tab>(initialTab);
  const [query, setQuery] = useState('');
  const [collection, setCollection] = useState('');
  const [sort, setSort] = useState<Sort>('default');
  const [own, setOwn] = useState<Own[]>([]);
  const newIds = useSaveDerived((s) => catalogNewIds(content, s));
  const isNew = (id: string) => newIds.includes(id);
  // "Mới" stays while the tab is open; leaving it (other tab, close, Đặt ngay) counts as seen.
  const markSeen = (ids: string[]) => {
    const fresh = ids.filter(isNew);
    if (fresh.length > 0) dispatch({ type: 'markCatalogSeen', ids: fresh });
  };
  const markTab = (which: Tab) => {
    markSeen(
      entriesFor(content, which)
        .filter(
          (e) => which !== 'themes' || collection === '' || (e.kind === 'item' && e.def.setId === collection),
        )
        .map((e) => e.def.id),
    );
  };
  const onClose = () => {
    markTab(tab);
    close();
  };
  const onPlaceNow = () => {
    markTab(tab);
    placeNow();
  };
  const tabsRef = useRef<HTMLDivElement>(null);
  // A remembered tab may sit past the edge of the scrolling tab row: bring it into view once on open.
  useEffect(() => {
    tabsRef.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, []);
  const [detail, setDetail] = useState<Entry | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [boxes, setBoxes] = useState(1);
  const [bought, setBought] = useState(false);
  // S06b confirm -> S06a detail -> list -> closed, one step per Back (ux-screens UX-03).
  useBack(() => {
    if (confirm) setConfirm(false);
    else if (detail) setDetail(null);
    else onClose();
  });

  // unlockedKey is a dependency so lock badges refresh when something unlocks.
  const tabEntries = useMemo(() => entriesFor(content, tab), [content, tab, unlockedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const ownedCount = (def: ItemDef) =>
    def.kind === 'product'
      ? (stacks[def.id as `prod_${string}`] ?? 0)
      : Object.values(instances).filter((i) => i.itemId === def.id).length;

  const reasonText = (missing: MissingReason[]): string => {
    const m = missing[0];
    if (!m) return t('ui.catalog.reason.other');
    if (m.kind === 'stars')
      return t('ui.catalog.reason.stars', { missing: m.need - m.have, need: m.need, have: m.have });
    if (m.kind === 'task') return t('ui.catalog.reason.tutorial');
    if (m.kind === 'unlock') return t('ui.catalog.reason.unlock');
    return t('ui.catalog.reason.other');
  };

  const lockOf = (e: Entry): string | null => {
    const gate = e.def.storyGate;
    if (gate && storyStage(store.getState(), gate.storyId) < gate.stage) {
      const story = content.neighborhoodStories.find((s) => s.id === gate.storyId);
      return t('neighborhood.catalog_locked', { name: story ? t(story.titleKey) : '', step: gate.stage });
    }
    const unlockId = e.def.unlockId;
    if (!unlockId || content.unlocks.size === 0) return null;
    const st = unlockStatus(content, store.getState(), unlockId);
    if (st.kind === 'granted') return null;
    return st.kind === 'locked' ? reasonText(st.missing) : t('ui.catalog.reason.other');
  };

  const priceOf = (e: Entry): number =>
    e.kind === 'wallfloor'
      ? (e.def.price ?? 0)
      : (e.def.buyPrice ?? 0) * (e.def.kind === 'product' ? content.balance.stockBoxSize * boxes : 1);

  const buy = (e: Entry) => {
    const txId = newTxId();
    const r =
      e.kind === 'wallfloor'
        ? dispatch({ type: 'buyWallFloor', txId, wallFloorId: e.def.id })
        : e.def.kind === 'product'
          ? dispatch({ type: 'buyStock', txId, itemId: e.def.id as `prod_${string}`, boxes })
          : dispatch({
              type: 'buyItem',
              txId,
              itemId: e.def.id as Exclude<ItemDef['id'], `prod_${string}`>,
              qty: 1,
            });
    setConfirm(false);
    if (r.ok) setBought(true);
  };

  const unitPrice = (e: Entry): number =>
    e.kind === 'wallfloor' ? (e.def.price ?? 0) : (e.def.buyPrice ?? 0);
  const isOwned = (e: Entry) => (e.kind === 'wallfloor' ? owned.includes(e.def.id) : ownedCount(e.def) > 0);
  const q = fold(query);
  const entries = tabEntries
    .filter((e) => tab !== 'themes' || collection === '' || (e.kind === 'item' && e.def.setId === collection))
    .filter((e) => q === '' || fold(t(e.def.nameKey)).includes(q))
    .filter((e) => {
      const ownership = own.filter((o) => o !== 'affordable');
      if (ownership.length > 0 && !ownership.some((o) => (o === 'owned') === isOwned(e))) return false;
      if (own.includes('affordable'))
        return lockOf(e) === null && !(e.kind === 'wallfloor' && isOwned(e)) && unitPrice(e) <= coins;
      return true;
    })
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      if (sort === 'priceUp') return unitPrice(a.e) - unitPrice(b.e) || a.i - b.i;
      if (sort === 'priceDown') return unitPrice(b.e) - unitPrice(a.e) || a.i - b.i;
      if (sort === 'new') return Number(isNew(b.e.def.id)) - Number(isNew(a.e.def.id)) || a.i - b.i;
      return a.i - b.i;
    })
    .map((x) => x.e);
  const filtering = q !== '' || own.length > 0;

  const open = (e: Entry) => {
    markSeen([e.def.id]);
    setDetail(e);
    setBought(false);
    setBoxes(1);
  };

  return (
    <div
      className={styles.scrim}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className={styles.sheet}
        data-catalog
        role="dialog"
        aria-modal="true"
        aria-label={t('ui.catalog.title')}
      >
        <div className={styles.grip} aria-hidden="true" />
        {!detail ? (
          <>
            <header className={styles.header}>
              <h2 className={styles.title}>{t('ui.catalog.title')}</h2>
              <span className={styles.coins}>
                <CoinIcon /> {t('ui.hud.coins', { count: coins })}
              </span>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label={t('ui.catalog.close')}
                onClick={onClose}
              >
                <Icon name="close" size={26} />
              </button>
            </header>
            <div className={styles.tabs} role="tablist" ref={tabsRef}>
              {TABS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={tab === id}
                  className={styles.tab}
                  data-testid={`catalog-tab-${id}`}
                  onClick={() => {
                    if (id !== tab) markTab(tab);
                    setTab(id);
                    onTabChange?.(id);
                  }}
                >
                  {t(`ui.catalog.tab.${id}`)}
                  {id !== tab && entriesFor(content, id).some((e) => isNew(e.def.id)) && (
                    <span className={styles.tabDot}>
                      <span className="visually-hidden">{t('ui.catalog.has_new')}</span>
                    </span>
                  )}
                </button>
              ))}
            </div>
            {tab === 'themes' && (
              <select
                className={styles.collectionSelect}
                aria-label={t('neighborhood.collection_filter')}
                value={collection}
                onChange={(e) => {
                  setCollection(e.target.value);
                }}
              >
                <option value="">{t('neighborhood.all_collections')}</option>
                {[...content.sets.values()].map((set) => (
                  <option key={set.id} value={set.id}>
                    {t(set.nameKey)}
                  </option>
                ))}
              </select>
            )}
            <div className={styles.tools}>
              <input
                type="search"
                className={styles.search}
                value={query}
                placeholder={t('ui.catalog.search')}
                aria-label={t('ui.catalog.search')}
                data-testid="catalog-search"
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
              />
              <select
                className={styles.sort}
                value={sort}
                aria-label={t('ui.catalog.sort')}
                data-testid="catalog-sort"
                onChange={(e) => {
                  setSort(e.target.value as Sort);
                }}
              >
                {SORTS.map((id) => (
                  <option key={id} value={id}>
                    {t(`ui.catalog.sort_${id}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.ownFilters} role="group" aria-label={t('ui.catalog.filters')}>
              {OWN_FILTERS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={styles.ownFilter}
                  aria-pressed={own.includes(id)}
                  data-testid={`catalog-filter-${id}`}
                  onClick={() => {
                    setOwn((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
                  }}
                >
                  {t(`ui.catalog.filter_${id}`)}
                </button>
              ))}
            </div>
            <ul className={styles.grid}>
              {entries.length === 0 && (
                <li className={styles.empty}>
                  <SpotArt name="empty_shop" fallback={filtering ? 'filter' : 'shopping'} size={110} />
                  {t(filtering ? 'ui.catalog.filter_empty' : 'ui.catalog.empty')}
                </li>
              )}
              {entries.map((e) => {
                const lock = lockOf(e);
                const isOwnedWall = e.kind === 'wallfloor' && owned.includes(e.def.id);
                const count = e.kind === 'item' ? ownedCount(e.def) : 0;
                return (
                  <li key={e.def.id}>
                    <button
                      type="button"
                      className={styles.card}
                      data-locked={lock !== null}
                      data-testid={`catalog-${e.def.id}`}
                      onClick={() => {
                        open(e);
                      }}
                    >
                      <span
                        className={styles.swatch}
                        style={{ background: e.kind === 'item' ? swatchFor(e.def) : 'var(--c-cream-deep)' }}
                        aria-hidden="true"
                      >
                        {e.kind === 'item' && <ItemThumb itemId={e.def.id} />}
                        {lock !== null && <Icon name="lock" size={22} className={styles.lock} />}
                      </span>
                      <span className={styles.name}>{t(e.def.nameKey)}</span>
                      <span className={styles.meta}>
                        {lock !== null
                          ? t('ui.catalog.locked')
                          : isOwnedWall
                            ? t('ui.catalog.applied')
                            : t('ui.catalog.price', {
                                price: e.kind === 'wallfloor' ? (e.def.price ?? 0) : (e.def.buyPrice ?? 0),
                              })}
                      </span>
                      {count > 0 && (
                        <span className={styles.badge}>{t('ui.catalog.owned_short', { count })}</span>
                      )}
                      {lock === null && isNew(e.def.id) && (
                        <span className={styles.new}>{t('ui.catalog.new')}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <Detail
            entry={detail}
            coins={coins}
            lock={lockOf(detail)}
            price={priceOf(detail)}
            boxes={boxes}
            owned={detail.kind === 'item' ? ownedCount(detail.def) : 0}
            wallOwned={detail.kind === 'wallfloor' && owned.includes(detail.def.id)}
            bought={bought}
            confirm={confirm}
            onBack={() => {
              setDetail(null);
            }}
            onClose={onClose}
            onBuy={() => {
              setConfirm(true);
            }}
            onConfirm={() => {
              buy(detail);
            }}
            onCancel={() => {
              setConfirm(false);
            }}
            onBoxes={setBoxes}
            onPlaceNow={onPlaceNow}
            onBuyMore={() => {
              setBought(false);
              setDetail(null);
            }}
            onApply={() => {
              if (detail.kind !== 'wallfloor') return;
              const room = currentRoom(store.getState());
              dispatch({ type: 'applyWallFloor', roomId: room, wallFloorId: detail.def.id });
            }}
          />
        )}
      </section>
    </div>
  );
}

interface DetailProps {
  entry: Entry;
  coins: number;
  lock: string | null;
  price: number;
  boxes: number;
  owned: number;
  wallOwned: boolean;
  bought: boolean;
  confirm: boolean;
  onBack: () => void;
  onClose: () => void;
  onBuy: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onBoxes: (n: number) => void;
  onPlaceNow: () => void;
  onBuyMore: () => void;
  onApply: () => void;
}

const VIEWS: Record<1 | 2 | 4, RotationKey[]> = {
  1: ['r0'],
  2: ['r0', 'r90'],
  4: ['r0', 'r90', 'r180', 'r270'],
};

function Detail(p: DetailProps) {
  const { t } = useI18n();
  const { content } = useServices();
  const def = p.entry.def;
  const item = p.entry.kind === 'item' ? p.entry.def : null;
  const views = VIEWS[item?.rotations ?? 1];
  const [view, setView] = useState(0);
  const set = item?.setId ? content.sets.get(item.setId) : undefined;
  const instances = useSave((s) => s.inventory.instances);
  const ownedIds = new Set(Object.values(instances).map((i) => i.itemId));
  const isProduct = item?.kind === 'product';
  const units = p.boxes * content.balance.stockBoxSize;
  const missingCoins = Math.max(0, p.price - p.coins);
  const likes = item?.homeTags[0]?.all
    .map((a) =>
      a.kind === 'zone'
        ? t(`ui.catalog.where.zone_${a.zone}`)
        : a.kind === 'on'
          ? t('ui.catalog.where.on')
          : a.kind === 'near'
            ? t('ui.catalog.where.near')
            : a.kind === 'wall'
              ? t('ui.catalog.where.wall')
              : t('ui.catalog.where.wall_adjacent'),
    )
    .join(', ');

  return (
    <div className={styles.detail}>
      <header className={styles.header}>
        <button type="button" className={styles.iconBtn} aria-label={t('ui.catalog.back')} onClick={p.onBack}>
          <Icon name="back" size={26} />
        </button>
        <h2 className={styles.title}>{t(def.nameKey)}</h2>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label={t('ui.catalog.close')}
          onClick={p.onClose}
        >
          <Icon name="close" size={26} />
        </button>
      </header>
      <div className={styles.previewWrap}>
        <div
          className={styles.preview}
          style={{ background: item ? swatchFor(item) : 'var(--c-cream-deep)' }}
          aria-hidden="true"
        >
          {item && <ItemThumb itemId={item.id} view={views[view] ?? 'r0'} />}
        </div>
        {views.length > 1 && (
          <button
            type="button"
            className={styles.turn}
            aria-label={t('ui.catalog.turn')}
            data-testid="catalog-turn"
            onClick={() => {
              setView((v) => (v + 1) % views.length);
            }}
          >
            <Icon name="rotate" size={28} />
          </button>
        )}
      </div>
      {set && (
        <p className={styles.line}>
          {t('ui.catalog.set_line', {
            name: t(set.nameKey),
            have: set.itemIds.filter((id) => ownedIds.has(id)).length,
            total: set.itemIds.length,
          })}
        </p>
      )}
      {item && <p className={styles.lore}>“{t(item.loreKey)}”</p>}
      {likes && <p className={styles.line}>{t('ui.catalog.likes', { where: likes })}</p>}
      {item && <p className={styles.line}>{t('ui.catalog.owned', { count: p.owned })}</p>}

      <div className={styles.footer}>
        {p.lock !== null ? (
          <p className={styles.reason}>
            {t('ui.catalog.locked')}. {t('ui.catalog.locked_reason', { reason: p.lock })}
          </p>
        ) : item && catalogAvailabilityNotSold(item) ? (
          <p className={styles.reason}>{t('ui.catalog.not_sold')}</p>
        ) : p.entry.kind === 'wallfloor' && p.wallOwned ? (
          <button type="button" className={styles.primary} onClick={p.onApply}>
            {t('ui.catalog.apply')}
          </button>
        ) : p.bought ? (
          <div className={styles.row}>
            <button type="button" className={styles.secondary} onClick={p.onBuyMore}>
              {t('ui.catalog.buy_more')}
            </button>
            <button
              type="button"
              className={styles.primary}
              data-tutorial-id="place-now"
              onClick={p.onPlaceNow}
            >
              {t('ui.catalog.place_now')}
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={styles.primary}
              disabled={missingCoins > 0}
              onClick={p.onBuy}
              data-testid="catalog-buy"
            >
              {t('ui.catalog.buy', { price: p.price })}
            </button>
            {missingCoins > 0 && (
              <p className={styles.reason}>{t('ui.catalog.not_enough', { missing: missingCoins })}</p>
            )}
          </>
        )}
      </div>

      {p.confirm && (
        <div className={styles.confirmScrim}>
          <div className={styles.confirm} role="alertdialog" aria-modal="true">
            <h3 className={styles.title}>{t('ui.catalog.confirm_title', { name: t(def.nameKey) })}</h3>
            {isProduct && (
              <div className={styles.stepper}>
                <button
                  type="button"
                  onClick={() => {
                    p.onBoxes(Math.max(1, p.boxes - 1));
                  }}
                  aria-label="−"
                >
                  <Icon name="minus" size={24} />
                </button>
                <span>{t('ui.catalog.boxes', { boxes: p.boxes, units })}</span>
                <button
                  type="button"
                  onClick={() => {
                    p.onBoxes(Math.min(5, p.boxes + 1));
                  }}
                  aria-label="+"
                >
                  <Icon name="plus" size={24} />
                </button>
              </div>
            )}
            <dl className={styles.sums}>
              <dt>{t('ui.catalog.confirm_price')}</dt>
              <dd>{t('ui.catalog.price', { price: p.price })}</dd>
              <dt>{t('ui.catalog.confirm_after')}</dt>
              <dd>{t('ui.catalog.price', { price: p.coins - p.price })}</dd>
            </dl>
            <div className={styles.row}>
              <button type="button" className={styles.secondary} onClick={p.onCancel}>
                {t('ui.catalog.later')}
              </button>
              <button
                type="button"
                className={styles.primary}
                disabled={p.price > p.coins}
                onClick={p.onConfirm}
                data-testid="catalog-confirm"
              >
                {t('ui.catalog.buy_short')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function catalogAvailabilityNotSold(def: ItemDef): boolean {
  return !def.sources.some((s) => s.kind === 'catalog') || def.buyPrice === undefined;
}
